/** Settings page: user preferences, detected performance profile, and (for
 * admins) the Update Manager. */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import type { PerformanceProfile, UpdateCheckRecord, UserSettings } from "@/types";

const STATUS_COLOR: Record<string, string> = {
  checked_up_to_date: "text-signal-teal",
  update_available: "text-signal-amber",
  applied: "text-signal-teal",
  failed: "text-signal-rose",
  rolled_back: "text-signal-amber",
};

export default function Settings(): React.ReactElement {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [performance, setPerformance] = useState<PerformanceProfile | null>(null);

  const [updateHistory, setUpdateHistory] = useState<UpdateCheckRecord[]>([]);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryMsg, setSummaryMsg] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryPreview, setSummaryPreview] = useState<{ subject: string; text: string } | null>(null);

  useEffect(() => {
    let isMounted = true;
    apiClient
      .get<UserSettings>("/settings")
      .then((response) => {
        if (isMounted) setSettings(response.data);
      })
      .catch(() => {
        if (isMounted) setError("Unable to load settings.");
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    apiClient
      .get<PerformanceProfile>("/system/performance")
      .then((response) => isMounted && setPerformance(response.data))
      .catch(() => undefined);

    if (user?.role === "admin") {
      apiClient
        .get<UpdateCheckRecord[]>("/system/updates/history")
        .then((response) => isMounted && setUpdateHistory(response.data))
        .catch(() => undefined);
    }

    return () => {
      isMounted = false;
    };
  }, [user?.role]);

  const handleSave = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!settings) return;
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { data } = await apiClient.patch<UserSettings>("/settings", {
        theme: settings.theme,
        locale: settings.locale,
        timezone: settings.timezone,
        email_notifications_enabled: settings.email_notifications_enabled,
        push_notifications_enabled: settings.push_notifications_enabled,
        performance_mode_override: settings.performance_mode_override,
        email_summary_enabled: settings.email_summary_enabled,
        email_summary_address: settings.email_summary_address,
      });
      setSettings(data);
      setSaved(true);
    } catch {
      setError("Unable to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheckForUpdates = async (): Promise<void> => {
    setIsCheckingUpdate(true);
    setUpdateError(null);
    try {
      const { data } = await apiClient.post<UpdateCheckRecord>("/system/updates/check");
      setUpdateHistory((prev) => [data, ...prev]);
    } catch {
      setUpdateError("Unable to check for updates. Confirm UPDATE_REPO_OWNER/UPDATE_REPO_NAME are set.");
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  const handlePreviewSummary = async (): Promise<void> => {
    setSummaryBusy(true);
    setSummaryError(null);
    setSummaryMsg(null);
    try {
      const { data } = await apiClient.get<{ subject: string; text: string }>("/summaries/weekly");
      setSummaryPreview(data);
    } catch {
      setSummaryError("Unable to build the summary. Add positions to a portfolio first.");
    } finally {
      setSummaryBusy(false);
    }
  };

  const handleSendSummary = async (): Promise<void> => {
    setSummaryBusy(true);
    setSummaryError(null);
    setSummaryMsg(null);
    try {
      const { data } = await apiClient.post<{ sent: boolean; to: string; detail?: string }>(
        "/summaries/weekly/send",
      );
      if (data.sent) {
        setSummaryMsg(`Summary sent to ${data.to}.`);
      } else {
        setSummaryError(data.detail ?? "Email delivery is not configured yet.");
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSummaryError(detail ? String(detail) : "The summary could not be sent.");
    } finally {
      setSummaryBusy(false);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-mist-400">Loading settings…</p>;
  }

  if (!settings) {
    return <p className="text-sm text-signal-rose">{error ?? "Settings unavailable."}</p>;
  }

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="mt-1 text-sm text-mist-400">Manage your workspace preferences.</p>
      </div>

      <form onSubmit={handleSave} className="panel space-y-5 p-6">
        {error && <p className="text-sm text-signal-rose">{error}</p>}
        {saved && <p className="text-sm text-signal-teal">Settings saved.</p>}

        <div>
          <label htmlFor="theme" className="field-label">
            Theme
          </label>
          <select
            id="theme"
            value={settings.theme}
            onChange={(event) => setSettings({ ...settings, theme: event.target.value })}
            className="field-input"
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </div>

        <div>
          <label htmlFor="timezone" className="field-label">
            Timezone
          </label>
          <input
            id="timezone"
            type="text"
            value={settings.timezone}
            onChange={(event) => setSettings({ ...settings, timezone: event.target.value })}
            className="field-input"
          />
        </div>

        <div className="flex items-center justify-between">
          <label htmlFor="emailNotifications" className="text-sm text-mist-200">
            Email notifications
          </label>
          <input
            id="emailNotifications"
            type="checkbox"
            checked={settings.email_notifications_enabled}
            onChange={(event) =>
              setSettings({ ...settings, email_notifications_enabled: event.target.checked })
            }
            className="h-4 w-4 accent-signal-teal"
          />
        </div>

        <div className="flex items-center justify-between">
          <label htmlFor="pushNotifications" className="text-sm text-mist-200">
            Push notifications
          </label>
          <input
            id="pushNotifications"
            type="checkbox"
            checked={settings.push_notifications_enabled}
            onChange={(event) =>
              setSettings({ ...settings, push_notifications_enabled: event.target.checked })
            }
            className="h-4 w-4 accent-signal-teal"
          />
        </div>

        <div>
          <label htmlFor="performanceOverride" className="field-label">
            Performance mode
          </label>
          <select
            id="performanceOverride"
            value={settings.performance_mode_override ?? "auto"}
            onChange={(event) =>
              setSettings({
                ...settings,
                performance_mode_override:
                  event.target.value === "auto"
                    ? null
                    : (event.target.value as UserSettings["performance_mode_override"]),
              })
            }
            className="field-input"
          >
            <option value="auto">Auto (recommended)</option>
            <option value="low">Low — fewer animations, lower chart FPS</option>
            <option value="medium">Medium — balanced</option>
            <option value="high">High — full animations and visuals</option>
          </select>
          {performance && (
            <p className="mt-1.5 text-xs text-mist-400">
              System auto-detected: <span className="capitalize">{performance.mode}</span> ({performance.reason})
            </p>
          )}
        </div>

        <button type="submit" disabled={isSaving} className="btn-primary w-full">
          {isSaving ? "Saving…" : "Save changes"}
        </button>
      </form>

      <div className="panel space-y-4 p-6">
        <div>
          <h2 className="text-sm font-semibold text-mist-100">Weekly email summary</h2>
          <p className="mt-1 text-xs text-mist-400">
            A recap of your portfolio's performance and risk predictions, emailed automatically
            once a week. Save your changes below before previewing or sending a test.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <label htmlFor="emailSummary" className="text-sm text-mist-200">
            Send weekly summary
          </label>
          <input
            id="emailSummary"
            type="checkbox"
            checked={settings.email_summary_enabled}
            onChange={(event) =>
              setSettings({ ...settings, email_summary_enabled: event.target.checked })
            }
            className="h-4 w-4 accent-signal-teal"
          />
        </div>

        <div>
          <label htmlFor="summaryAddress" className="field-label">
            Send to
          </label>
          <input
            id="summaryAddress"
            type="email"
            value={settings.email_summary_address ?? ""}
            placeholder={user?.email ?? "you@example.com"}
            onChange={(event) =>
              setSettings({ ...settings, email_summary_address: event.target.value || null })
            }
            className="field-input"
          />
          <p className="mt-1 text-xs text-mist-500">
            Leave blank to use your account email{user?.email ? ` (${user.email})` : ""}.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handlePreviewSummary()}
            disabled={summaryBusy}
            className="btn-secondary text-xs"
          >
            Preview summary
          </button>
          <button
            type="button"
            onClick={() => void handleSendSummary()}
            disabled={summaryBusy}
            className="btn-secondary text-xs"
          >
            Send test now
          </button>
        </div>

        {summaryMsg && <p className="text-xs text-signal-teal">{summaryMsg}</p>}
        {summaryError && <p className="text-xs text-signal-rose">{summaryError}</p>}

        {summaryPreview && (
          <div className="rounded-lg border border-ink-700 bg-ink-800/40 p-4">
            <p className="text-xs font-medium text-mist-200">{summaryPreview.subject}</p>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-mist-300">
              {summaryPreview.text}
            </pre>
          </div>
        )}
      </div>

      {performance && (
        <div className="panel space-y-2 p-6">
          <h2 className="text-sm font-semibold text-mist-100">Detected hardware</h2>
          <div className="grid grid-cols-2 gap-2 text-xs text-mist-400">
            <p>OS: <span className="text-mist-200">{performance.os_name}</span></p>
            <p>CPU cores: <span className="text-mist-200">{performance.cpu_logical_cores ?? "unknown"}</span></p>
            <p>RAM: <span className="text-mist-200">{performance.total_ram_gb ? `${performance.total_ram_gb} GB` : "unknown"}</span></p>
            <p>GPU: <span className="text-mist-200">{performance.gpu_name ?? "not detected"}</span></p>
          </div>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="panel space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-mist-100">Software updates</h2>
            <button
              onClick={handleCheckForUpdates}
              disabled={isCheckingUpdate}
              className="btn-secondary text-xs"
            >
              {isCheckingUpdate ? "Checking…" : "Check for updates"}
            </button>
          </div>
          {updateError && <p className="text-xs text-signal-rose">{updateError}</p>}
          {updateHistory.length === 0 ? (
            <p className="text-xs text-mist-400">No update checks recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {updateHistory.slice(0, 5).map((record) => (
                <li key={record.id} className="text-xs">
                  <span className={STATUS_COLOR[record.status] ?? "text-mist-300"}>
                    {record.status.replace(/_/g, " ")}
                  </span>
                  {record.latest_version && (
                    <span className="text-mist-400"> — v{record.latest_version}</span>
                  )}
                  <span className="text-mist-500">
                    {" "}
                    ({new Date(record.checked_at).toLocaleString()})
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs italic text-mist-400">
            Checking for updates never modifies your installation. Preparing and applying an
            update requires a separate, explicit confirmation and always backs up first.
          </p>
        </div>
      )}
    </div>
  );
}
